<script>
	/* By Connor Rothschild https://twitter.com/CL_Rothschild
	Scrollytelling component from Russell Goldenberg https://twitter.com/codenberg/status/1432774653139984387 */
	
  import Scrolly from "./Scrolly.svelte";
  import Maps from "./Maps.svelte";
  import Azovstal from "./Azovstal.svelte";
	import PageProgress from "svelte-page-progress";

  export let animation = true;
	
  let value;
  const steps = [
		"<p>Depuis le début de la guerre fin février, l'armée russe concentre son offensive sur les grandes villes ukrainiennes, dont <span style='text-decoration: underline;text-decoration-color: #D34848;text-decoration-thickness: 4px;'>Marioupol</span>, ici en bas à droite de la carte.</p>",
    "<p>Le 2 mars, sept jours après le début de l’offensive, l'artillerie russe pilonne cette ville russophone de 441.000 habitants, située à environ 55 kilomètres de la frontière russe, en bordure des <span style='background-color: #D34848'>régions séparatistes</span> de Donetsk et Louhansk.</p>",
    "<p>La prise de Marioupol permettrait à la Russie d'assurer la jonction entre ses forces en provenance de la Crimée annexée, qui ont déjà pris les ports clés de <span style='text-decoration: underline;text-decoration-color: #D34848;text-decoration-thickness: 4px;'>Kherson</span> et de <span style='text-decoration: underline;text-decoration-color: #D34848;text-decoration-thickness: 4px;'>Berdiansk</span>, et les troupes séparatistes et russes du Donbass plus au nord.</p>",
		"<p>Le 9 mars, une frappe russe vise une maternité et hôpital pédiatrique de la ville, faisant trois morts. L'Ukraine et l'UE condamnent un «&nbsp<i>crime de guerre</i>&nbsp», la Russie affirmant elle que le bâtiment abritait des combattants ultranationalistes ukrainiens.</p>",
		"<p>Mi-mars, après plusieurs tentatives infructueuses, des milliers de civils commencent à être évacués de Marioupol via <span style='text-decoration: underline;text-decoration-color: #D34848;text-decoration-thickness: 4px;'>un couloir humanitaire</span> en direction de la ville de Zaporijia située à plus de 200 km à l'ouest.</p>",
		"<p>Le 16 mars, un théâtre, où près d'un millier de personnes s'étaient réfugiées, est détruit par l'aviation russe, selon les autorités ukrainiennes qui indiqueront ensuite redouter la mort d'environ 300 personnes. Moscou dément ce bombardement, l'attribuant au bataillon nationaliste ukrainien Azov.</p>"
  ];
	  const steps_2 = [
		"<p>L’attention se concentre alors sur l’usine Azovstal, gigantesque enchevêtrement d’acier à l’est de Marioupol. </p>",
		"<p>Le site, l’une des plus grandes aciéries d’Europe, comporte des kilomètres de galeries où des militaires et des civils se sont réfugiés.</p>",
		"<p>Selon un représentant des forces séparatistes prorusses de Donetsk, «&nbsp<i>c'est une ville dans la ville, et il y a plusieurs niveaux souterrains datant de la période soviétique, ce n'est pas possible de bombarder d'en haut, il faut nettoyer sous terre. Cela prendra du temps</i>&nbsp».</p>",
  ];


</script>
<PageProgress color="linear-gradient(90deg, #C2544E 0%, #ba1007 100%)" />

<div class='hero'>
		<h1> Que se passe-t-il à Marioupol&nbsp?<br>La bataille pour cette ville stratégique racontée en cartes</h1>
		<p>Marioupol, port du sud-est de l'Ukraine, est assiégée depuis début mars par les forces russes. La ville a été «&nbsp<i>libérée</i>&nbsp», a affirmé Vladimir Poutine le 21 avril dernier. Des militaires et des civils ukrainiens sont pourtant toujours retranchés dans le site industriel d’Azovstal. Marioupol, où la situation humanitaire est catastrophique, est aujourd’hui largement en ruines. Focus sur la situation et les enjeux de ce point clé de la guerre.</p>
    <hr>
    <h2>Récit et mise en page<br>Ambroise Carton avec AFP</h2>
    <img src="./pics/arrow.svg" class="arrow" class:bounce={animation} alt="" aria-hidden="true"/>
	</div>

  <div class="section-container">
    <div class="steps-container">
      <Scrolly bind:value>
        {#each steps as text, i}
          <div class="step" class:active={value === i}>
            <div class="step-content">{@html text}</div>
          </div>
        {/each}
        <div class="spacer" />
      </Scrolly>
    </div>
    <div class="sticky">
      <Maps step={value} />
    </div>
  </div>

  <div class="section-container">
  <div class="row">
    <div class="column">

<figure><img src="./pics/theatre1.jpg" alt='Le théâtre de Marioupol après son bombardement le 16 mars 2022. Le mot «&nbsp<i>enfant</i>&nbsp» est écrit en grandes lettres blanches sur l’esplanade devant le bâtiment. - ' style="width:100%"><figcaption>Le théâtre de Marioupol après son bombardement le 16 mars 2022. Le mot «&nbsp<i>enfant</i>&nbsp» est écrit en grandes lettres blanches sur l’esplanade devant le bâtiment - AFP PHOTO / Satellite image ©2022 Maxar Technologies</figcaption></figure>
    </div>
      <div class="column">

<figure><img src="./pics/theatre2.jpg" alt="Un soldat russe dans les ruines du théâtre de Marioupol le 12 avril 2022." style="width:100%"><figcaption>Un soldat russe dans les ruines du théâtre de Marioupol le 12 avril 2022 - Alexander Nemenov / AFP</figcaption></figure>
    </div>
  </div>
</div>
<div class="blockquote-wrapper"><div class="blockquote"><h1>«Marioupol a dépassé le stade de la catastrophe humanitaire»</h1><h4>- le maire de la ville, le 4 avril 2022</h4></div></div>

<div class="section-container-text">
  
  <h1>Résistance et «&nbsp<i>régime de silence</i>&nbsp»</h1>
	<p>Le 21 mars, Kiev rejette un ultimatum de Moscou qui demandait la capitulation de Marioupol. Les civils qui ont réussi à quitter la ville avec leurs propres véhicules décrivent des bombardements incessants, des habitants terrés dans des caves et des cadavres jonchant les rues sous des bombardements incessants.  </p>
  <p>Le 30, Moscou annonce un «&nbsp<i>régime de silence</i>&nbsp» (cessez-le-feu local), à partir du lendemain matin afin d'ouvrir un couloir humanitaire. </p>
  <p>Pendant plusieurs jours, Russes et Ukrainiens se rejettent la responsabilité quant aux difficultés rencontrées dans des évacuations vers Zaporijia. Dans la nuit du 1er au 2 avril, le président ukrainien indique néanmoins que plus de 3000 personnes ont pu être "sauvées".</p>
  <p>Le 4 avril, le maire de Marioupol déclare que la ville est détruite «&nbsp<i>à 90%</i>&nbsp» et qu'elle a «&nbsp<i>dépassé le stade de la catastrophe humanitaire</i>&nbsp». Le 7, les autorités pro-russes évaluent les pertes civiles à environ 5000 personnes et les logements détruits à «&nbsp<i>60-70%</i>&nbsp». Le «&nbsp<i>nouveau maire</i>&nbsp» proclamé la veille par les forces pro-russes estime que 250.000 personnes ont quitté Marioupol mais qu'au moins autant, voire 300.000, restent sur place. L'Ukraine évalue plutôt à 100.000 le nombre de personnes encore en ville. </p> 

  <div class="flourish-embed flourish-photo-slider" data-src="visualisation/9621180"></div>

<p>Le 11 avril, alors que Joe Biden parle pour la première fois de «&nbsp<i>génocide</i>&nbsp» en Ukraine, l'armée ukrainienne dit se préparer à «&nbsp<i>une ultime bataille</i>&nbsp» à Marioupol. Les séparatistes revendiquent avoir conquis la zone portuaire. Deux jours plus tard, le ministère de la Défense russe affirme que 1026 soldats ukrainiens «&nbsp<i>ont volontairement déposé les armes et se sont rendus</i>&nbsp» aux forces russes.</p>
</div>
<div class="section-container">
  <div class="steps-container">
    <Scrolly bind:value>
      {#each steps_2 as text, i}
        <div class="step" class:active={value === i}>
          <div class="step-content">{@html text}</div>
        </div>
      {/each}
      <div class="spacer" />
    </Scrolly>
  </div>
  <div class="sticky">
    <Azovstal step={value} />
  </div>
</div>
<img src="./pics/desktop/7.webp" style="width: 100%;height: auto;" alt="Map">
<div class="section-container-text">
  <div class="blockquote-wrapper"><div class="blockquote"><h1>«Vous avez probablement vu beaucoup de choses dans votre vie. Mais je suis sûr que vous n'avez jamais vu ce qui se passe ici à Marioupol. Parce qu'ici c'est l'enfer sur terre»</h1><h4>- Serguiï Volyna, commandant de la 36e brigade de la marine ukrainienne <a href="https://www.rtbf.be/article/c-est-l-enfer-sur-terre-sauvez-les-civils-la-lettre-dechirante-du-commandant-des-marines-ukrainiens-au-pape-francois-10976745" target="_blank">dans une lettre au pape François</a></h4></div></div>
  <p><a href="https://www.facebook.com/serjvlk/posts/3268353776825125" target="_blank">Dans une vidéo postée sur Facebook le 20 avril</a>, Serguiï Volyna, commandant de la 36e brigade de la marine ukrainienne, lance un appel à l’aide : «&nbsp<i>Nous vivons peut-être nos derniers jours, voire nos dernières heures L'ennemi est 10 fois plus nombreux que nous. Nous appelons et supplions tous les dirigeants du monde de nous aider. Nous leur demandons d'utiliser la procédure d'extraction et de nous emmener sur le territoire d'un pays tiers.</i>&nbsp»</p>
  <p>La veille, le conseil municipal de Marioupol déclarait qu’au moins un millier de civils, surtout des femmes, enfants et personnes âgées, sont terrés avec les combattants. Selon la même source, 20.000 habitants de la ville ont été tués en un mois et demi de combats.</p>
  <h1>«<i>La menace immédiate de perdre Marioupol s'est dissipée</i></h1>
  <p>Le 22 avril, le président ukrainien Volodymyr Zelensky résumait la situation dans un discours prononcé devant le parlement du Portugal. «&nbsp<i>Il n'y a pas un seul bâtiment non endommagé à Marioupol. Une ville littéralement brûlée</i>&nbsp», a-t-il déclaré. Et d’ajouter : «&nbsp<i>Pendant plus d'un mois, les troupes russes ont assiégé Marioupol (...) Des centaines de milliers de civils étaient (coincés) là, sans nourriture, sans eau, sans médicaments. Sous des bombardements constants.</i>&nbsp»</p>
  <p>Des évacuations ont malgré tout été organisées. Les responsables ukrainiens assurent cependant avoir voulu évacuer beaucoup plus de civils, mais accusent les forces russes d'avoir ciblé une route utilisée par des personnes fuyant les combats. </p>
  <p>Le 22 avril toujours, Oleksiy Arestovytch, conseiller du président ukrainien, se voulait optimiste. «&nbsp<i>La menace immédiate de perdre Marioupol s'est dissipée</i>&nbsp». Selon lui, Moscou n'a pas assez de troupes pour encercler, et donc assiéger, l'aciérie.</p>
  <h1>«&nbsp<i>Que pas une mouche ne passe</i>&nbsp»</h1>
  <p>Trois jours plus tard, la Russie accusait les autorités ukrainiennes d'avoir empêché les civils terrés à Azovstal de quitter ces lieux, malgré l'annonce d'un cessez-le-feu par l'armée russe. Les catégories de personnes autorisées à sortir étaient les femmes, les enfants et le personnel de l'usine.</p> 
  <p>Dans la soirée du 25 avril, l'armée russe a affirmé dans un communiqué que «&nbsp<i>toujours personne ne s'est servi du couloir humanitaire proposé.</i>&nbsp» La Russie qui ajoute cette accusation : «&nbsp<i>Les autorités de Kiev ont de nouveau fait saper de manière cynique cette opération humanitaire.&nbsp»</p>
  <p>Azovstal est désormais l’un des principaux nœuds du conflit. L'Ukraine a prévenu qu'elle romprait les négociations avec la Russie si ses derniers combattants présents dans l’usine étaient tués. Quelques jours plus tôt, le président russe Vladimir Poutine lançait cet ordre à son armée : «&nbsp<i>que pas une mouche</i>&nbsp» ne sorte de cette forteresse assiégée.</p>
  </div>
  <footer class="footer"><h1>Retrouvez tous nos articles<br>sur la guerre en Ukraine <a href="https://www.rtbf.be/archive/crise-en-ukraine" target="_blank">en cliquant ici</a></h1></footer>


<style>
 @import url('https://fonts.googleapis.com/css2?family=Libre+Bodoni:wght@700&family=Montserrat:wght@700&family=PT+Sans&display=swap');

 :root {
  font-size: 62.5%; /* (62.5/100) * 16px = 10px */
  --font-size--large: 4rem; /* 24px */
  --font-size--small: 2rem; /* 14px */
  --font-size--default: 1.6rem; /* 16px */
  --font-size--extrasmall: 1rem; /* 16px */

}

	:global(body) {
		overflow-x: hidden;
		background-color: #F4F4F4;
    margin: 0px;
    padding: 0px;
	}

.footer {
  display: flex;
  flex-flow: row wrap;
  padding: 30px 30px 20px 30px;
  color: #2f2f2f;
  background-color: #fff;
  border-top: 1px solid #e5e5e5;
}

.footer h1 {
  font-family: 'PT Sans', sans-serif;
    font-size: var(--font-size--large);
    margin:auto;
    text-align:center;
}
	
	.hero {
		display: flex;
		place-items: center;
		flex-direction: column;
		justify-content: center;
	}

  .hero h1 {
    font-family: 'Libre Bodoni', serif;
    font-size: var(--font-size--large);
    margin-left: 10vw;
    margin-right:10vw;
    text-align:center;
  }

  .hero p {
    font-family: 'Libre Bodoni', serif;
    font-size: var(--font-size--small);
    margin-left: 20vw;
    margin-right:20vw;
    text-align:center;
	}

  .hero hr{
    background-color: black;
    border: 0 none;
    height: 1px;
    width:50%;
}

.hero h2 {
  font-family: 'Libre Bodoni', serif;
  font-size: 0.8em;
  font-weight: 700;
  text-transform: uppercase;
  text-align:center;
  font-size: var(--font-size--default);
}

.section-container-text {
      flex-direction: column-reverse;
    }

    .section-container-text h1 {
    font-family: 'Libre Bodoni', serif;
    font-size: var(--font-size--large);
    margin-left: 25vw;
    margin-right:25vw;
  }
  

.section-container-text p {
    font-family: 'PT Sans', sans-serif;
    font-size: var(--font-size--small);
    margin-left: 25vw;
    margin-right:25vw;
    text-align:left;
	}
	
  .spacer {
    height: 40vh;
  }

  .sticky {
    position: sticky;
    top: 0;
		flex: 1 1 60%;
    width: 60%;
  }

  .section-container {
    margin-top: 1em;
    text-align: center;
    display: flex;
    width: 100%;
  }

  .step {
    height: 100vh;
    display: flex;
    place-items: center;
    justify-content: center;
  }

  .step-content {
    font-size: var(--font-size--small);
    font-family: 'PT Sans', sans-serif;
		background: white;
		color: black;
    padding: 0.2em 0.5em 0.2em 0.5em;
    display: flex;
    flex-direction: column;
    justify-content: center;
    box-shadow: 1px 1px 10px rgba(0, 0, 0, .2);
    text-align: center;
		width: 35%;
		margin-left: 200px; margin-right: auto;
		max-width: 500px;
  }

	.step.active .step-content {
		background: white;
		color: black;
	}
	
  .steps-container,
  .sticky {
    height: 100%;
  }

  .steps-container {
    flex: 1 1 40%;
    z-index: 10;
  }
	
    .section-container {
      flex-direction: column-reverse;
    }
    .sticky {
      width: 100%;
			margin: auto;
    }


    * {
  box-sizing: border-box;
}

figure{
  margin:0;
}

.column {
  float: left;
  width: 50%;
  padding: 5px;
  text-align:right;
  font-size: var(--font-size--extrasmall);
  font-family: 'PT Sans', sans-serif;
}

/* Clearfix (clear floats) */
.row::after {
  content: "";
  clear: both;
}

/* Responsive layout - makes the three columns stack on top of each other instead of next to each other */
@media screen and (max-width: 550px) {
  .column {
    width: 100%;
  }
  .hero p {
    font-size: var(--font-size--small);
    margin-left: 10vw;
    margin-right:10vw;
    text-align:center;
	}
  .step-content {
      margin: auto;
      width: 70%;
}
.section-container-text h1 {
    font-size: var(--font-size--small);
    margin-left: 30px;
    margin-right:30px;
  }
  

.section-container-text p {
    font-size: var(--font-size--small);
    margin-left: 30px;
    margin-right:30px;
	}

  .footer h1 {
  font-size: var(--font-size--default);
}
.blockquote {
    margin-left: 10% !important;
}
.blockquote h1 {
    font-size: var(--font-size--small);
}
.blockquote h4 {
    font-size: var(--font-size--extrasmall);
}
}

    .arrow {
		width: 48px;
		height: 48px;
	}

	.bounce {
		-webkit-animation-duration: 2s;
		        animation-duration: 2s;
		-webkit-animation-iteration-count: infinite;
		        animation-iteration-count: infinite;
		-webkit-animation-name: bounce;
		        animation-name: bounce;
		-webkit-animation-timing-function: ease;
		        animation-timing-function: ease;
	}
	@-webkit-keyframes bounce {
		0%   { -webkit-transform: translateY(10px); transform: translateY(10px); }
		30%  { -webkit-transform: translateY(-10px); transform: translateY(-10px); }
		50%  { -webkit-transform: translateY(10px); transform: translateY(10px); }
		100% { -webkit-transform: translateY(10px); transform: translateY(10px); }
	}
	@keyframes bounce {
		0%   { -webkit-transform: translateY(10px); transform: translateY(10px); }
		30%  { -webkit-transform: translateY(-10px); transform: translateY(-10px); }
		50%  { -webkit-transform: translateY(10px); transform: translateY(10px); }
		100% { -webkit-transform: translateY(10px); transform: translateY(10px); }
	}

  /* center the blockquote in the page */
.blockquote-wrapper {
   display: flex;
}

/* Blockquote main style */
.blockquote {
    position: relative;
    font-family: 'Libre Bodoni', serif;
    font-weight: 800;
    color: black;
    width: 100%;
    max-width: 600px;
    z-index: 1;
    margin-left: 30vw;
    margin-right: 25vw;
    align-self: center;
}

/* Blockquote header */
.blockquote h1 {
    position: relative;
    color: black;
    font-size: var(--font-size--small);
    font-weight: 800;
    line-height: 1.2;
    margin: 20px;
    border-left: 4px solid #CCC;
    padding-left: 8px;
}

/* Blockquote subheader */
.blockquote h4 {
    position: relative;
    color: #292a2b;
    font-size: var(--font-size--default);
    font-weight: normal;
    line-height: 1;
    margin: 20px;
    padding-top: 3px;
    z-index: 1;
}

</style>
